import { themeAtom } from "../../shared/atoms";
import {
  voyageChecklistCountsAtom,
  voyageChecklistTabLabels,
  VoyageChecklistTabLabel,
} from "../atoms/checklistCounts.atoms";
import type { VoyageStatus } from "../models/voyage.interfaces";
import AtDischargePort from "./AtDischargePort";
import AtLoadPort from "./AtLoadPort";
import PreVoyagePlan from "./PreVoyagePlan";
import { Tab, Tabs, TabsHandle } from "./TabControl";
import VoyageToDischargePort from "./VoyageToDischargePort";
import VoyageToLoadPort from "./VoyageToLoadPort";
import { useAtomValue } from "jotai";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

type Props = {
  realtimeStatus: VoyageStatus | null;
};

const VoyageDetailsChecklistTab: React.FC<Props> = ({ realtimeStatus }) => {
  const theme = useAtomValue(themeAtom);
  const checklistCounts = useAtomValue(voyageChecklistCountsAtom);

  const tabRef = useRef<TabsHandle>(null);
  const hasInitializedRealtimeTab = useRef(false);
  const [activeTab, setActiveTab] = useState<VoyageChecklistTabLabel>(voyageChecklistTabLabels.preVoyagePlan);

  useEffect(() => {
    if (!realtimeStatus || hasInitializedRealtimeTab.current) return;

    tabRef.current?.setActiveTab(realtimeStatus);
    hasInitializedRealtimeTab.current = true;
  }, [realtimeStatus]);

  const activeChecklistCount = checklistCounts[activeTab] ?? { checked: 0, total: 0 };
  const completionText = `${activeChecklistCount.checked}/${activeChecklistCount.total} completed`;

  return (
    <div
      className={twMerge(
        "flex max-h-[675px] w-full flex-col overflow-hidden rounded-lg border",
        "[&_.voyage-checklist-body>div:first-child]:hidden",
        "[&_.voyage-checklist-body>div:nth-child(2)>p:first-child]:hidden",
        theme.home.tailwind.panelBackground,
        theme.uwhcAssistant.chatUi.panelBorder,
      )}
    >
      <div
        className={twMerge(
          "rounded-t-lg border-b px-6 py-4",
          theme.uwhcAssistant.chatUi.panelBorder,
          theme.opsKnowledgeAssistant.chatUi.headerBg,
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <h2 className={twMerge("text-lg font-bold", theme.home.tailwind.textDefault)}>Checklist</h2>
          <span
            className={twMerge(
              "rounded-full border px-3 py-1 text-sm font-medium leading-5",
              theme.uwhcAssistant.chatUi.connectionConnected,
            )}
          >
            {completionText}
          </span>
        </div>
      </div>

      <div className="h-full overflow-auto px-6 py-5">
        {/* TODO: All the tab children components code is copy pasted, 95% is the same, we should refactor it. */}
        <Tabs
          ref={tabRef}
          variant="voyageChecklist"
          onActiveTabChange={(label) => setActiveTab(label as VoyageChecklistTabLabel)}
        >
          <Tab label={voyageChecklistTabLabels.preVoyagePlan}>
            <PreVoyagePlan />
          </Tab>
          <Tab label={voyageChecklistTabLabels.voyageToLoadPort}>
            <VoyageToLoadPort />
          </Tab>
          <Tab label={voyageChecklistTabLabels.atLoadPort}>
            <AtLoadPort />
          </Tab>
          <Tab label={voyageChecklistTabLabels.voyageToDischargePort}>
            <VoyageToDischargePort />
          </Tab>
          <Tab label={voyageChecklistTabLabels.atDischargePort}>
            <AtDischargePort />
          </Tab>
        </Tabs>
      </div>
    </div>
  );
};

export default VoyageDetailsChecklistTab;
